'use client'

import { ClubJoinBlock } from 'cms-types'
import { useState } from 'react'
import { ClubJoin as ClubJoinUI } from 'ui'

import { resolveRelation } from '@/cms/helpers'
import { logger } from '@/helpers/logger'
import { ClubMembershipStatus, useClubMembership } from '@/hooks/useClubMembership'
import { useCustomer } from '@/store/customerStore'

type JoinStatus = 'idle' | 'joining' | 'success' | 'error'

export function ClubJoinClient({ data }: { data: ClubJoinBlock }) {
  const membership = useClubMembership()
  const refresh = useCustomer(s => s.refresh)
  const [status, setStatus] = useState<JoinStatus>('idle')

  // Only non-members see the join UI. While the customer store hydrates we render
  // nothing (rather than flashing the join hero at actual members).
  if (membership !== ClubMembershipStatus.NonMember) return null

  const handleJoin = async () => {
    setStatus('joining')

    try {
      const response = await fetch('/api/club/join', { method: 'POST' })
      if (!response.ok) throw new Error(`Join request failed with status ${response.status}`)

      setStatus('success')

      // Membership is granted out of band, so the `Club:Member` tag may not be
      // present yet. We refresh to pick it up when available, but keep the success
      // confirmation regardless.
      try {
        await refresh()
      } catch (error) {
        logger.error('ClubJoin > refresh', error)
      }
    } catch (error) {
      logger.error('ClubJoin > join', error)
      setStatus('error')
    }
  }

  return (
    <ClubJoinUI
      data={{
        title: data.title,
        subtitle: data.subtitle,
        benefitsTitle: data.benefitsTitle ?? undefined,
        benefits: data.benefits ?? [],
        joinButtonLabel: data.joinButtonLabel,
        image: resolveRelation(data.image),
        successTitle: data.successTitle,
        successText: data.successText,
        errorText: data.errorText,
        status,
        onJoin: handleJoin,
      }}
    />
  )
}
